import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import Image from '@alltrails/core/components/Image';
import ShimmerComponents from '@alltrails/core/components/ShimmerComponents';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import getUserProfilePhotoUrl from '@alltrails/core/utils/getUserProfilePhotoUrl';
import PhotoSizeDimensions from '@alltrails/types/PhotoSizeDimensions';
import type { LinkInfo, Size } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import CustomAvatar from './CustomAvatar';
import styles from './styles/styles.module.scss';
import AvatarFlair, { type Flair } from './AvatarFlair';

const { ProfilePhotoShimmer } = ShimmerComponents;

type AvatarProps = {
  bordered?: boolean;
  className?: string;
  eager?: boolean;
  linkInfo?: LinkInfo;
  onClick?: () => void;
  size?: Size<'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'>;
  testId?: string;
  userId: number;
  userName: string;
  stopPropagation?: boolean;
  flair?: Flair;
};

export default function Avatar({
  bordered = false,
  className,
  eager = false,
  linkInfo,
  onClick,
  size = 'md',
  testId,
  userId,
  userName,
  stopPropagation,
  flair
}: AvatarProps) {
  const intl = useIntl();
  const [src, setSrc] = useState('');
  const [didError, setDidError] = useState(false);
  const publicBaseUrl = usePublicBaseUrl();

  const photoSize = useMemo<PhotoSizeDimensions>(() => {
    switch (size) {
      case 'xxs':
      case 'xs':
        return '50x50';
      case 'sm':
        return '80x80';
      case 'lg':
      case 'xl':
        return '160x160';
      case 'md':
      default:
        return '120x120';
    }
  }, [size]);

  useEffect(() => {
    setSrc(getUserProfilePhotoUrl({ baseUrl: publicBaseUrl, id: userId, size: photoSize }));
  }, [publicBaseUrl, userId, photoSize]);

  return (
    <CustomAvatar
      {...setDenaliAttribute('Avatar')}
      bordered={bordered}
      className={classNames(className, styles.userAvatar)}
      linkInfo={linkInfo}
      onClick={onClick}
      size={size}
      testId={testId}
      stopPropagation={stopPropagation}
    >
      {src === '' ? (
        <ShimmerContainer>
          <ProfilePhotoShimmer className={styles[size]} />
        </ShimmerContainer>
      ) : (
        <>
          <span className={styles.imageWrapper}>
            <Image
              className={styles.avatarPhoto}
              // Override the loader so we can explicitly just use the `src` as-is. This will ignore default quality and width arguments from Next. Our image API does not support them and will return a 400.
              loader={({ src }) => src}
              onError={() => setDidError(true)}
              src={didError ? '' : src}
              alt={intl.formatMessage({ defaultMessage: 'Avatar of {name}' }, { name: userName })}
              loading={eager ? 'eager' : 'lazy'}
              fill
            />
            {flair && <AvatarFlair flair={flair} size={size} />}
          </span>
        </>
      )}
    </CustomAvatar>
  );
}
